import { Button } from 'react-daisyui';
import { useTranslation } from 'react-i18next';
import { eSeatQuoteUnavailable } from 'src/providers/SubscriptionProvider/useSeatPurchase.types';
import { eSeatBillingMode, type tSeatCostNoticeProps } from './SeatCostNotice.types';
import { formatSeatAmount } from './SeatCostNotice.functions';

/**
 * What this seat costs, said inside the form the customer is already filling in.
 *
 * Nobody wakes up wanting to buy a seat — they want their colleague — so the charge is
 * a consequence to disclose, not a storefront. Three things must always be true here:
 *
 *  - the recurring per-seat amount is stated, and that it keeps recurring until the
 *    seat is removed on the Subscription tab;
 *  - the money-timing matches what billing will actually do, per billing mode;
 *  - a price that cannot be resolved is never rendered as zero or free.
 */
export default function SeatCostNotice(props: tSeatCostNoticeProps) {
  const { t } = useTranslation('settings/team');
  const {
    quote,
    amountDueToday,
    isLoadingAmountDueToday,
    billingMode,
    onGoToSubscription,
  } = props;

  // R16 — say why, and offer somewhere to go. A disabled control with no explanation
  // is the dead end this whole change exists to remove.
  if (!quote.available || !quote.purchasable || billingMode === eSeatBillingMode.UNKNOWN) {
    const reasonKey =
      billingMode === eSeatBillingMode.UNKNOWN
        ? 'page.seatCost.noQuote.billingUnknown'
        : (quote.available && !quote.purchasable) ||
            (!quote.available && quote.reason === eSeatQuoteUnavailable.SUBSCRIPTION_NOT_ELIGIBLE)
        ? 'page.seatCost.noQuote.subscription'
        : 'page.seatCost.noQuote.generic';

    return (
      <section
        className="TeamSection__Modal__SeatCost"
        data-testid="seat-cost-notice"
        aria-live="polite"
      >
        <p
          className="TeamSection__Modal__SeatCost__reason"
          data-testid="seat-cost-no-quote"
        >
          {t(reasonKey)}
        </p>
        <Button
          type="button"
          size="sm"
          color="ghost"
          className="TeamSection__Modal__SeatCost__link"
          data-testid="seat-cost-subscription-link"
          onClick={onGoToSubscription}
        >
          {t('page.form.button.manage-seats-on-subscription.text')}
        </Button>
      </section>
    );
  }

  const recurring = formatSeatAmount(quote.recurringAmountCents, quote.currency);

  return (
    <section
      className="TeamSection__Modal__SeatCost"
      data-testid="seat-cost-notice"
      // The notice appears mid-form once the workspace turns out to be full, and the
      // submit label's amount changes under it. Both are announced.
      aria-live="polite"
    >
      <p
        className="TeamSection__Modal__SeatCost__recurring"
        data-testid="seat-cost-recurring"
      >
        {t('page.seatCost.recurring', { amount: recurring })}
      </p>

      {billingMode === eSeatBillingMode.CARD && (
        <p data-testid="seat-cost-timing-card">
          {isLoadingAmountDueToday
            ? t('page.seatCost.today.loading')
            : amountDueToday.known
              ? t('page.seatCost.today.amount', {
                  amount: formatSeatAmount(
                    amountDueToday.amountCents,
                    amountDueToday.currency,
                  ),
                })
              : // The preview is the only source for this figure; without it we say
                // nothing rather than invent one that the invoice will contradict.
                t('page.seatCost.today.unavailable')}
        </p>
      )}

      {billingMode === eSeatBillingMode.TRIALING && (
        <p data-testid="seat-cost-timing-trial">
          {t('page.seatCost.trial')}
        </p>
      )}

      {billingMode === eSeatBillingMode.INVOICE && (
        <p data-testid="seat-cost-timing-invoice">
          {t('page.seatCost.invoice')}
        </p>
      )}

      <p
        className="TeamSection__Modal__SeatCost__ownership"
        data-testid="seat-cost-ownership"
      >
        {t('page.seatCost.ownership')}
      </p>
    </section>
  );
}
